// frontend/src/components/ScheduleDetails.js

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Edit, Delete, LucideCopy, BookX, LucideCheckCircle, LucideArrowLeftRight, LucideMinusCircle, X } from 'lucide-react';
 // For date and time formatting
import ScheduleApplyModal from './ScheduleApplyModal';
import { formatDate, formatTimeString } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { isSystemAdmin, isAdmin } from '../utils/roleUtils';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';


const ScheduleDetails = ({
  schedule,
  currentEntity,
  onApply,
  onUnApply,
  onRefresh,
  selectedRoute,
  isApplied, // Receive isApplied prop
  onUpdatePriority,
  onSelectSchedule,
  openEditModal,
  openCopyModal,
  appliedSchedules = [],
  onDeleteSchedule,
  onScheduleChanged,
  conflicts, // New prop: list of conflicting schedule IDs
  scheduleMap, // New prop: map of schedule IDs to schedule objects
  getScheduleSnapshot, // New prop: function to get a snapshot of the schedule
  allEntities = [],
  allRoutes = [],
  appliedRouteIds = [], // Array of route IDs where this schedule is applied
  onClose,
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  // Force re-render when preferences change
  useDateTimePreferences();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false); // state for apply modal
  // initialize state for isScheduleApplied when the component mounts
  const [isScheduleAppliedToAny, setIsScheduleAppliedToAny] = useState(schedule.isScheduleApplied);

  const userId = parseInt(localStorage.getItem('userId'), 10);

  console.log('Schedule here:', schedule);

  // const isScheduleApplied = Number(schedule.isScheduleApplied);

  console.log('schedule.isScheduleApplied:', schedule.isScheduleApplied);
  
  // Determine if the user can edit the schedule
  // System admins can edit any schedule, entity admins can only edit their own
  const canEdit =
    isSystemAdmin(currentEntity.userRole) ||
    (isAdmin(currentEntity.userRole) && schedule.created_by === userId);

  // Format dates and times
/*   const formatDate = (dateStr) => {
    if (!dateStr) {
      return t('N/A');
    }
    const date = moment(dateStr, ['YYYY-MM-DD', moment.ISO_8601]);
    if (!date.isValid()) {
      return t('Invalid Date');
    }
    // Use the date formatter utility that respects user preferences
    return formatDateUtil(date.toDate());
  }; */

  console.log('canEdit:', canEdit);

/*   const formatTime = (timeStr) => {
    if (!timeStr) {
      return t('N/A');
    }
    const time = moment(timeStr, ['HH:mm:ss', 'HH:mm']);
    if (!time.isValid()) {
      return t('Invalid Time');
    }
    return time.format('HH:mm');
  }; */

  // CSS classes for styling - now using theme
  const labelClassName = cn('block text-sm font-semibold', text.primary);
  const valueClassName = cn('text-sm mt-1 pl-2', text.secondary);

  // Ensure that time_slots and excluded_periods are arrays
  const timeSlots = Array.isArray(schedule.time_slots) ? schedule.time_slots : [];
  const excludedPeriods = Array.isArray(schedule.excluded_periods) ? schedule.excluded_periods : [];

  // Handler for the apply modal close action
  // update the isScheduleAppliedToAny state when the modal is closed
  const handleApplyModalClose = (hasAppliedRoutes) => {
    if (hasAppliedRoutes !== undefined) {
      // Convert boolean to 1/0 to match the API format
      setIsScheduleAppliedToAny(hasAppliedRoutes ? 1 : 0);
    }
    setShowApplyModal(false);
    onRefresh();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={cn(
        "rounded-lg shadow-xl w-full md:w-4/5 lg:w-1/2 h-full flex flex-col mx-4",
        background.primary,
        border.default,
        "border"
      )}>
        {/* Header */}
        <div className={cn(
          "flex justify-between items-center p-6",
          border.default,
          "border-b"
        )}>
          <h3 className={cn("text-xl font-bold", text.primary)}>
            {schedule.name || t('Untitled Schedule')}
          </h3>
          <button
            onClick={onClose}
            className={cn(
              "p-2 rounded-lg transition-colors",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              text.secondary
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Schedule Details */}
        <div className="p-6 overflow-y-auto flex-grow">
          <div className="space-y-6">
            {/* Schedule Type */}
            <div>
              <label className={labelClassName}>{t('Schedule Type')}</label>
              <p className={valueClassName}>
                {schedule.type === 'fixed' ? t('Fixed Schedule') : t('On-Call Schedule')}
              </p>
            </div>

            {/* Start Date */}
            <div>
              <label className={labelClassName}>{t('Start Date')}</label>
              <p className={valueClassName}>{formatDate(schedule.start_date)}</p>
            </div>

            {/* End Date */}
            <div>
              <label className={labelClassName}>{t('End Date')}</label>
              <p className={valueClassName}>{formatDate(schedule.end_date)}</p>
            </div>

            {/* Included Days of Week */}
            <div>
              <label className={labelClassName}>{t('Included Days of Week')}</label>
              <p className={valueClassName}>
                {schedule.included_days
                  ? schedule.included_days
                      .split(',')
                      .map((dayId) => t(getDayName(parseInt(dayId))))
                      .join(', ')
                  : t('All Days')}
              </p>
            </div>

            {/* Time Slots or On-Call Details */}
            {schedule.type === 'on_call' ? (
              <div className="space-y-4">
                {/* On-Call Start Time */}
                <div>
                  <label className={labelClassName}>{t('On-Call Start Time')}</label>
                  <p className={valueClassName}>{formatTimeString(schedule.on_call_start_time)}</p>
                </div>

                {/* On-Call End Time */}
                <div>
                  <label className={labelClassName}>{t('On-Call End Time')}</label>
                  <p className={valueClassName}>{formatTimeString(schedule.on_call_end_time)}</p>
                </div>

                {/* Minimum Gap Between Trips */}
                <div>
                  <label className={labelClassName}>{t('Minimum Gap Between Trips (minutes)')}</label>
                  <p className={valueClassName}>{schedule.on_call_min_gap || t('N/A')}</p>
                </div>

                {/* On-Call Description */}
                <div>
                  <p className="text-sm text-gray-600">
                    {t('This on-call schedule operates from')} {formatTimeString(schedule.on_call_start_time)} {t('to')}{' '}
                    {formatTimeString(schedule.on_call_end_time)} {t('daily. The minimum gap between trips is')}{' '}
                    {schedule.on_call_min_gap} {t('minutes.')}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label className={labelClassName}>{t('Time Slots')}</label>
                {timeSlots.length > 0 ? (
                  timeSlots.map((slot, index) => (
                    <div key={index} className="mb-4">
                      <p className={valueClassName}>
                        <strong>{t('Type')}: </strong>
                        {slot.type === 'single' ? t('Single Trip') : t('Recurring Trips')}
                      </p>
                      <div style={{ marginLeft: '20px' }}>
                        {slot.type === 'single' ? (
                          <p className={valueClassName}>
                            <strong>{t('Start Time')}: </strong>
                            {formatTimeString(slot.start_time)}
                          </p>
                        ) : (
                          <>
                            <p className={valueClassName}>
                              <strong>{t('Start Time')}: </strong>
                              {formatTimeString(slot.start_time)}
                            </p>
                            <p className={valueClassName}>
                              <strong>{t('End Time')}: </strong>
                              {formatTimeString(slot.end_time)}
                            </p>
                            <p className={valueClassName}>
                              <strong>{t('Frequency (minutes)')}: </strong>
                              {slot.frequency}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className={valueClassName}>{t('No Time Slots')}</p>
                )}
              </div>
            )}

            {/* Excluded Periods */}
            <div>
              <label className={labelClassName}>{t('Excluded Periods')}</label>
              {excludedPeriods.length > 0 ? (
                excludedPeriods.map((period, index) => (
                  <div key={index} className="mb-2">
                    <p className={valueClassName}>
                      {formatDate(period.start_date || period.start)} -{' '}
                      {formatDate(period.end_date || period.end)} ({period.label})
                    </p>
                  </div>
                ))
              ) : (
                <p className={valueClassName}>{t('No Excluded Periods')}</p>
              )}
            </div>

            {/* Common Schedule */}
            <div>
              <label className={labelClassName}>{t('Common Schedule')}</label>
              <p className={valueClassName}>
                {schedule.is_common ? t('Yes, available for all entities') : t('No')}
              </p>
            </div>

            {/* Predefined Schedule */}
  {/*           <div>
              <label className={labelClassName}>{t('Predefined Schedule')}</label>
              <p className={valueClassName}>{schedule.is_predefined ? t('Yes') : t('No')}</p>
            </div> */}

            {/* created by */}
            <div>
              <label className={labelClassName}>{t('Schedule Created by: ')}</label>
              <p className={valueClassName}>
                {schedule.created_by === userId ? t('Me') : t('Other User')}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        
        <div className={cn(
          "flex flex-wrap justify-end items-center gap-3 p-6",
          border.default,
          "border-t"
        )}>

          {/* Copy Button - always visible */}
          <button
            onClick={() => {
              openCopyModal(schedule);              
            }}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
              "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            )}
          >
            <LucideCopy size={16} />
            {t('Copy')}
          </button>
          
          <>
            {/* Edit Button */}
            <button
              onClick={() => {
                if (canEdit && isScheduleAppliedToAny === 0) {
                  openEditModal(schedule);                  
                }
              }}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm",
                (!canEdit || isScheduleAppliedToAny === 1)
                  ? "bg-gray-400 cursor-not-allowed text-gray-700"
                  : "bg-yellow-500 hover:bg-yellow-600 text-white"
              )}
              disabled={!canEdit || isScheduleAppliedToAny === 1}              
              title={
                (isAdmin(currentEntity.userRole) && !isSystemAdmin(currentEntity.userRole) && schedule.created_by !== userId)
                  ? t("Schedule is created by other user, can't be edited")
                  : isScheduleAppliedToAny === 1
                  ? t("Schedule is applied, can't be edited")
                  : ''
              }
            >
              <Edit size={16} />
              {t('Edit')}
            </button>

            {/* Delete Button */}
            <button
              onClick={() => {
                if (canEdit && isScheduleAppliedToAny === 0) {
                  setConfirmationAction('delete');
                  setShowConfirmation(true);
                }
              }}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm",
                (!canEdit || isScheduleAppliedToAny === 1)
                  ? "bg-gray-400 cursor-not-allowed text-gray-700"
                  : "bg-red-500 hover:bg-red-600 text-white"
              )}
              disabled={!canEdit || isScheduleAppliedToAny === 1}
              title={
                (isAdmin(currentEntity.userRole) && !isSystemAdmin(currentEntity.userRole) && schedule.created_by !== userId)
                  ? t("Schedule is created by other user, can't be deleted")
                  : isScheduleAppliedToAny === 1
                  ? t("Schedule is applied, can't be deleted")
                  : ''
              }
            >
              <Delete size={16} />
              {t('Del')}
            </button>
          </>
            
          {/* Apply/Unapply Button */}
          <button
            onClick={() => setShowApplyModal(true)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm",
              "bg-green-600 hover:bg-green-700 text-white"
            )}
          >
            <div className="flex items-center gap-1">
              <LucideCheckCircle size={16} />
              <LucideArrowLeftRight size={16} />
              <LucideMinusCircle size={16} />
            </div>
            {t('(Un)Apply...')}
          </button>
          {/* Add the modal component */}
          {showApplyModal && (
            <ScheduleApplyModal
              schedule={schedule}
              currentEntity={currentEntity}
              onClose={handleApplyModalClose}
              onApply={onApply}                
              onUnApply={onUnApply}              
              currentUserId={userId}              
            />
          )}

          {/* Close Button */}
          <button
            onClick={onClose} 
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm",
              "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600",
              "text-gray-800 dark:text-gray-200"
            )}
          >
            <BookX size={16} />
            {t('Close')}
          </button>
        </div>
      </div>

      <ConfirmDialogShell
        isOpen={showConfirmation && confirmationAction === 'delete'}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => {
          onDeleteSchedule(currentEntity.entityId, schedule.id);
          setShowConfirmation(false);
          onClose();
        }}
        title={t('Confirm Delete')}
        confirmLabel={t('delete') || t('Confirm')}
        confirmVariant="danger"
        confirmIcon={Delete}
        showIcon={true}
        iconVariant="danger"
      >
        <p className="text-sm text-red-500">
          {t('areYouSure')}
        </p>
      </ConfirmDialogShell>

    </div>   
    
  );
};

// Helper function to get day name from day ID
const getDayName = (dayId) => {
  const daysOfWeek = [
    { id: 0, name: 'Sunday' },
    { id: 1, name: 'Monday' },
    { id: 2, name: 'Tuesday' },
    { id: 3, name: 'Wednesday' },
    { id: 4, name: 'Thursday' },
    { id: 5, name: 'Friday' },
    { id: 6, name: 'Saturday' },
  ];
  const day = daysOfWeek.find((d) => d.id === dayId);
  return day ? day.name : '';
};

export default ScheduleDetails;
